import {
  MenuButton as ChakraMenuButton,
  Button,
  Flex,
  Spacer,
  Text,
  MenuItemOption,
  Menu as ChakraMenu,
  AbsoluteCenter,
  Box,
  Divider,
  MenuList as ChakraMenuList,
  MenuOptionGroup,
  MenuItem,
} from '@chakra-ui/react';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { IconType } from 'react-icons';
import { BiChevronDown } from 'react-icons/bi';
import { RiTodoLine } from 'react-icons/ri';
import {
  FilterCondition,
  ValidationCriteria,
  ValidationFilterCondition,
} from '../../../sheet/types';
import { CustomDivider } from './types';
import useSheetTheme from '../../../sheet/hooks/useSheetTheme';
import { getContrastBackgroundColor1 } from '../../utils/view';

type CustomOptions =
  | FilterCondition
  | ValidationCriteria
  | ValidationFilterCondition
  | string;

interface MenuButtonProps {
  Icon?: React.ReactNode;
  title: string;
  TitleView?: React.ReactNode;
  bg?: string;
  isDisabled?: boolean;
}
const MenuButton = ({
  Icon,
  title,
  TitleView,
  bg,
  isDisabled,
}: MenuButtonProps) => (
  <ChakraMenuButton
    as={Button}
    rounded="2xl"
    width="100%"
    size="sm"
    fontWeight="medium"
    fontSize="14px"
    background={bg}
    isDisabled={isDisabled}
  >
    <Flex alignItems="center">
      {Icon && Icon}
      {/* <Icon /> */}
      <Spacer />
      <Text textTransform="capitalize">
        {TitleView ? TitleView : title.toLowerCase()}
      </Text>
      <Spacer />
      <BiChevronDown />
    </Flex>
  </ChakraMenuButton>
);

// interface MenuItemViewProps<T extends CustomOptions> {
//   value: T;
//   selectedValue?: T;
//   selectedRef?: React.ForwardedRef<HTMLButtonElement | null>;
// }
// const MenuItemView = <T extends CustomOptions>({
//   value,
//   selectedValue,
//   selectedRef,
// }: MenuItemViewProps<T>) => {
//   console.log('==>value', value);
//   return (
//     <MenuItemOption
//       // key={value}
//       value={value}
//       // ref={value === selectedValue ? selectedRef : null}
//       // textTransform="capitalize"
//     >
//       {value.toLowerCase()}
//     </MenuItemOption>
//   );
// };

interface MenuProps {
  children: React.ReactNode;
  //   selectedRef: React.Foe<HTMLButtonElement | null>;
}

const Menu = forwardRef(function ({ children }: MenuProps, ref) {
  const initialFocusRef = useRef(null);
  useImperativeHandle(ref, () => initialFocusRef.current);

  return (
    <ChakraMenu
      matchWidth
      preventOverflow
      boundary="scrollParent"
      initialFocusRef={initialFocusRef}
    >
      {children}
    </ChakraMenu>
  );
});

interface MenuDividerWithNameProps {
  bg: string;
  title: string;
}
const MenuDividerWithName = ({ bg, title }: MenuDividerWithNameProps) => {
  return (
    <Box position="relative" padding="1" my="2">
      <Divider />
      <AbsoluteCenter
        bg={bg}
        px="4"
        rounded="2xl"
        textTransform="uppercase"
        // fontSize="13px"
      >
        <Text fontSize="12px">{title}</Text>
      </AbsoluteCenter>
    </Box>
  );
};

export interface OptionsWithIcons {
  name: string;
  Icon: IconType;
}

interface MenuListProps<T> {
  includeNone?: boolean;
  options?: (T | CustomDivider)[];
  optionsWithIcons?: (OptionsWithIcons | CustomDivider)[];
  selectedValue?: T;
  changeValue: (value: T) => void;
  selectedRef?: React.ForwardedRef<HTMLButtonElement | null>;
  customOptionView?: (value: T) => JSX.Element;
}
const MenuList = <T extends CustomOptions>({
  options,
  includeNone,
  selectedValue,
  optionsWithIcons,
  changeValue,
  selectedRef,
  customOptionView,
}: MenuListProps<T>) => {
  const { isDarkTheme, stroke } = useSheetTheme();
  const bg = getContrastBackgroundColor1(stroke, isDarkTheme);

  const renderOptionsWithIcons = () =>
    optionsWithIcons &&
    optionsWithIcons.map((option) => {
      if ('title' in option) {
        const title = option.title;
        return <MenuDividerWithName bg={bg} key={title} title={title} />;
      }

      const name = option.name;
      const Icon = option.Icon;
      return (
        <MenuItemOption
          value={name}
          key={name}
          textTransform="capitalize"
          ref={name === selectedValue ? selectedRef : null}
        >
          <Flex alignItems="center">
            <Icon />
            <Text ml="10px">{name}</Text>
          </Flex>
        </MenuItemOption>
      );
    });

  const renderOptions = () =>
    options &&
    options.map((option) => {
      if (typeof option === 'object') {
        const title = option.title;
        return <MenuDividerWithName bg={bg} key={title} title={title} />;
      }
      return (
        <MenuItemOption
          value={option}
          key={option}
          textTransform="capitalize"
          ref={option === selectedValue ? selectedRef : null}
        >
          {customOptionView ? customOptionView(option) : option.toLowerCase()}
        </MenuItemOption>
      );
    });

  return (
    <ChakraMenuList maxH="500px" overflowY="auto" className="custom-scrollbar">
      <MenuOptionGroup
        defaultValue=""
        type="radio"
        value={selectedValue}
        onChange={(e) => changeValue(e as T)}
      >
        {includeNone && <MenuItemOption value="">None</MenuItemOption>}
        {renderOptionsWithIcons()}
        {renderOptions()}
      </MenuOptionGroup>
    </ChakraMenuList>
  );
};

export default {
  Menu,
  MenuList,
  MenuButton,
  MenuDividerWithName,
};
